import type { Container } from "@/context/AppContext";

/**
 * Walk `parentId` from a container up to its root, returning the ordered nodes
 * ancestors → self (e.g. `[Shop, Case A, Bin B-014]`). Powers the TNK-189
 * "where is this card?" lookup, which renders the full physical path to a bin.
 *
 * Cycle-bounded (mirrors the server's `wouldCycle` guard) so a malformed chain
 * can't loop forever; an unknown id stops the walk. Pure — unit-tested.
 */
export function containerPath(
  containerId: string,
  byId: Map<string, Container>,
): Container[] {
  const path: Container[] = [];
  const seen = new Set<string>();
  let cursor: string | undefined = containerId;
  for (let i = 0; i < 64 && cursor; i++) {
    if (seen.has(cursor)) break; // cycle guard
    seen.add(cursor);
    const node = byId.get(cursor);
    if (!node) break; // unknown id — stop with what we have
    path.unshift(node);
    cursor = node.parentId;
  }
  return path;
}

/** Join a path's names: `"Shop ▸ Case A ▸ A1-C1-R1"`. */
export function formatContainerPath(nodes: Container[]): string {
  return nodes.map((n) => n.name).join(" ▸ ");
}
